import { formatListName } from "@/shared/utils/formatListName";

/**
 * Hands out names that are unique across everything the user will see.
 *
 * The filter panel merges portfolios, tag watchlists and combined watchlists
 * into one "Watchlists" list, so two records sharing a name are two
 * indistinguishable rows there however different they are underneath. A plan
 * that builds a portfolio and a combined watchlist over it will happily call
 * both "Gold Watch" — which is how a colleague ended up with a duplicate row
 * they could not tell apart.
 *
 * Compared on the display form, since that is what the user reads: names are
 * trimmed, whitespace-collapsed and first-letter-capitalised before saving, so
 * "gold watch" and "Gold  Watch" are the same row to them.
 */
export function makeNameClaimer(taken: Iterable<string>) {
  const norm = (n: string) => formatListName(n).toLowerCase();
  const seen = new Set<string>();
  for (const name of taken) seen.add(norm(name));

  return (desired: string): string => {
    const base = formatListName(desired) || "Untitled watchlist";
    if (!seen.has(norm(base))) {
      seen.add(norm(base));
      return base;
    }
    // " 2", " 3" … rather than "(copy)": these are peers built in one go, not
    // copies of each other.
    for (let n = 2; n < 1000; n += 1) {
      const candidate = `${base} ${n}`;
      if (!seen.has(norm(candidate))) {
        seen.add(norm(candidate));
        return candidate;
      }
    }
    return base;
  };
}
